// File: typescript/apps/front-end/src/main/react/series/CreateSeriesFormLazy.tsx
//
// Lazy boundary for CreateSeriesForm. The form is zod-backed (validation/collections), and the series
// settings dialog is statically reachable from the eager sidebar chrome — so without this wrapper
// `zod` ships in main_bundle.js on every page. Loading it lazily moves zod + the form into an
// on-demand chunk that loads only when the create/update series dialog opens. Drop-in default
// replacement for `import CreateSeriesForm`; UpdateSeriesSettingsDialog only changes its import path.
// See designs/01-now/2026-06-29-mobile-cwv-field-regression-...-design.md (N1).

import React, {Suspense} from "react";
import {lazyWithRetry} from "@/lib/lazyWithRetry";
import {LazyControlBoundary} from "@/main/react/LazyControlBoundary";
import {RecoverableIslandFallback} from "@/main/react/RecoverableIslandFallback";

type CreateSeriesFormProps = React.ComponentProps<(typeof import("@/main/react/series/CreateSeriesForm"))["default"]>;

const CreateSeriesFormImpl = lazyWithRetry(
    () => import("@/main/react/series/CreateSeriesForm"),
    "create-series-form",
);

function CreateSeriesFormFallback() {
    return (
        <div className="w-full animate-pulse space-y-4 py-2" aria-hidden="true">
            <div className="h-10 w-full rounded-xl bg-muted" />
            <div className="h-24 w-full rounded-2xl bg-muted" />
            <div className="h-10 w-1/3 rounded-xl bg-muted" />
        </div>
    );
}

export default function CreateSeriesForm(props: CreateSeriesFormProps) {
    // Containment (FI-070, 2026-09-15): a chunk that never arrives costs this control, not the island
    // around it. The boundary sits OUTSIDE the Suspense, where the lazy rejection is re-thrown to. The
    // retry card is the honest fallback here: this control IS what the member came for, and a form or
    // editor that silently never appears is a member wondering why nothing happened. Its Retry reloads
    // the page, which is exactly what recovers a chunk that has since become available.
    return (
        <LazyControlBoundary controlName="create-series-form" fallback={<RecoverableIslandFallback />}>
            <Suspense fallback={<CreateSeriesFormFallback />}>
                <CreateSeriesFormImpl {...props} />
            </Suspense>
        </LazyControlBoundary>
    );
}
